import React, { useEffect, useState } from 'react';
import { QueryObserverResult, RefetchOptions, useMutation } from '@tanstack/react-query';
import { User } from '@/types/TAccounts';
import { Button, notification, Spin } from 'antd';
import VoiceLibraryCard from '@/pages/ReceptionistDashboard/VoiceLibrary/voiceLibraryCard';
import { useVoiceModels } from 'src/Hooks/useVoiceModels';
import { TVoiceConfig } from '@/types/TVoiceModel';
import { updateVoiceModal } from '@/api/voice';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { defaultVoiceModel } from '@/utils/defaultVoiceModal';
import { useDispatch } from 'react-redux';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { ChevronRightOutlined } from '@mui/icons-material';
import { safeJSONParse } from '@/utils/helper';

interface VoiceSelected {
	voiceId: string;
	provider: string;
	voiceConfig: TVoiceConfig;
}

export const FeaturedVoicesInfo: React.FC<{
	data: User | undefined;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	debrand?: boolean;
}> = ({ data, refetch }) => {
	const dispatch = useDispatch();

	const language = 'English';

	const [selectedCard, setSelectedCard] = useState<string | null>(null);
	const [englishVoiceConfig, setEnglishVoiceConfig] = useState<VoiceSelected | null>();
	const [otherVoiceConfig, setOtherVoiceConfig] = useState<VoiceSelected | null>();
	const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState<number | null>(null);

	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const {
		voiceModels,
		voiceModelsFeatured,
		voiceModel,
		voiceModelMultilingual,
		isPending: isPendingVoiceModels,
		refetch: refetchVoiceModels,
	} = useVoiceModels({
		searchText: '',
		selectedCard: null,
		voiceId: englishVoiceConfig?.voiceId,
		voiceIdMultilingual: otherVoiceConfig?.voiceId,
	});

	const { mutate: updateVoiceModalMutation } = useMutation({
		mutationKey: ['updateVoiceModal'],
		mutationFn: updateVoiceModal,
		onSuccess() {
			refetch();
			refetchVoiceModels();
			notification.success({ message: 'Voice model updated successfully!' });
		},
		onError() {
			notification.error({ message: 'Some error occured during voice model updation' });
		},
	});

	useEffect(() => {
		if (data?.english_voice_selected || data?.other_language_voice_selected) {
			const englishVoiceSelected: VoiceSelected | null = safeJSONParse(
				data?.english_voice_selected,
				null,
			);
			const otherLanguageVoiceSelected: VoiceSelected | null = safeJSONParse(
				data?.other_language_voice_selected,
				null,
			);

			setEnglishVoiceConfig(englishVoiceSelected);
			setOtherVoiceConfig(otherLanguageVoiceSelected);

			setSelectedCard(englishVoiceSelected?.voiceId || null);
		}
	}, [data, language, refetch, FeaturedVoicesInfo, voiceModels, voiceModelsFeatured]);

	const handlePlayClick = (index: number | null) => {
		setCurrentlyPlayingIndex(index);
	};

	const determineInitialVoiceModel = ({
		voiceId,
		language,
		provider,
		model,
	}: {
		voiceId: string;
		language: string;
		provider: string;
		model?: string;
	}) => {
		if (englishVoiceConfig?.voiceId === voiceId) {
			return (
				englishVoiceConfig?.voiceConfig?.voiceModel ||
				model ||
				defaultVoiceModel({ language, provider })
			);
		} else if (otherVoiceConfig?.voiceId === voiceId) {
			return (
				otherVoiceConfig?.voiceConfig?.voiceModel ||
				model ||
				defaultVoiceModel({ language, provider })
			);
		}
		return model || defaultVoiceModel({ language, provider });
	};

	const handleApplyClick = async (
		cardValue: string,
		selectedVoiceModel: string,
		selectedVoiceProvider: string,
		voiceConfig: TVoiceConfig,
		model?: string,
	) => {
		setSelectedCard(cardValue);
		const voiceSelected = JSON.stringify({
			voiceId: selectedVoiceModel,
			provider: selectedVoiceProvider,
			voiceConfig: voiceConfig || {
				voiceModel: determineInitialVoiceModel({
					voiceId: selectedVoiceModel,
					language,
					provider: selectedVoiceProvider,
					model,
				}),
			},
		});

		const targetLanguage = language;

		updateVoiceModalMutation({
			voiceSelected,
			language: targetLanguage,
			agentId: selectedAgentId as string,
		});
	};

	return (
		<div>
			<Spin spinning={isPendingVoiceModels}>
				<main className="w-full px-4 sm:px-6 lg:px-8">
					<div className="w-full flex flex-col md:flex-row items-center">
						{voiceModel && (
							<div className="w-full">
								<h2 className="text-lg font-semibold text-gray-500">Selected English Voice</h2>
								<div className="w-full md:w-2/3">
									<VoiceLibraryCard
										key={voiceModel.voiceId}
										index={0}
										data={voiceModel}
										isSelected={true}
										englishVoiceConfig={englishVoiceConfig}
										otherVoiceConfig={otherVoiceConfig}
										handleApplyClick={handleApplyClick}
										onApplyClick={() => {}}
										currentlyPlayingIndex={currentlyPlayingIndex}
										onPlayClick={handlePlayClick}
										user={data}
										deleteVoiceMutation={() => {}}
										isFeatured={true}
										voiceModalCredits={null}
										fromConfig={true}
									/>
								</div>
							</div>
						)}
						{voiceModelMultilingual && (
							<div className="w-full">
								<h2 className="text-lg font-semibold text-gray-500">Selected Multilingual Voice</h2>
								<div className="w-full md:w-2/3">
									<VoiceLibraryCard
										key={voiceModelMultilingual.voiceId}
										index={0}
										data={voiceModelMultilingual}
										isSelected={true}
										englishVoiceConfig={englishVoiceConfig}
										otherVoiceConfig={otherVoiceConfig}
										handleApplyClick={handleApplyClick}
										onApplyClick={() => {}}
										currentlyPlayingIndex={currentlyPlayingIndex}
										onPlayClick={handlePlayClick}
										user={data}
										deleteVoiceMutation={() => {}}
										isFeatured={true}
										voiceModalCredits={null}
										fromConfig={true}
									/>
								</div>
							</div>
						)}
					</div>
					<div className="mt-8">
						<h2 className="text-lg font-semibold text-gray-500">Featured Voices</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{voiceModelsFeatured?.map((item, index) => (
								<VoiceLibraryCard
									key={item.voiceId}
									index={index}
									data={item}
									isSelected={selectedCard === item.voiceId}
									englishVoiceConfig={englishVoiceConfig}
									otherVoiceConfig={otherVoiceConfig}
									handleApplyClick={handleApplyClick}
									onApplyClick={() =>
										handleApplyClick(
											item.voiceId,
											item.voiceId,
											item.provider,
											item?.voiceConfig,
											item.model,
										)
									}
									currentlyPlayingIndex={currentlyPlayingIndex}
									onPlayClick={handlePlayClick}
									user={data}
									deleteVoiceMutation={() => {}}
									isFeatured={true}
									voiceModalCredits={null}
									fromConfig={true}
								/>
							))}
						</div>
					</div>
					<div className="w-full flex justify-end">
						<Button
							onClick={() => dispatch(setCurrentPage(ReceptionistDashboardSiderItem.VOICE_LIBRARY))}
							className="bg-white text-blue-600 hover:bg-gray-50 text-sm sm:text-md sm:w-auto"
						>
							Explore More Voices <ChevronRightOutlined />
						</Button>
					</div>
				</main>
			</Spin>
		</div>
	);
};
